// noinspection JSUnusedLocalSymbols

import { Injectable } from '@angular/core';
import { BaseViewModel } from '../../models/base/base-view-model';
import { Company } from '../../models/company/dto/company';
import { Router } from '@angular/router';
import { CompanyDomainModel } from '../../domainModels/company-domain-model';
import { BehaviorSubject, combineLatest, defer, Observable } from 'rxjs';
import { debounceTime, map, shareReplay } from 'rxjs/operators';
import { MenuDomainModel } from '../../domainModels/menu-domain-model';
import { SearchUtils } from '../../utils/search-utils';
import { CompaniesAndLocations } from '../shared/components/companies-and-location/companies-and-locations';
import { exists } from '../../functions/exists';
import { PaginationUtils } from '../../utils/pagination-utils';
import { InventoryProvider } from '../../utils/inventory-provider-type';
import { SortUtils } from '../../utils/sort-utils';

// Provided by Logged In Scope
@Injectable()
export class CustomersViewModel extends BaseViewModel {
  companies$ = this.domainModel.companies$;
  public inventoryProviders$ = window.types.inventoryProviders$;

  private _numberOfCompaniesPerPage = new BehaviorSubject<number>(10);
  public numberOfCompaniesPerPage$ = defer(() => this._numberOfCompaniesPerPage);
  setNumberOfCompaniesPerPage = (value: number): void => this._numberOfCompaniesPerPage.next(value);
  public nCompaniesPerPageOptions$ = new BehaviorSubject(
    PaginationUtils.constructResultsPerPageOptions([10, 25, 50, 100])
  );

  private _selectedCompany: BehaviorSubject<Company> = new BehaviorSubject<Company>(null);
  public selectedCompany$ = this._selectedCompany as Observable<Company>;
  connectToSelectedCompany = (c: Company) => this._selectedCompany.next(c);

  companyIdToSelect$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  private listenToCompanySelection = combineLatest([this.companies$.notNull(), this.companyIdToSelect$]).subscribe(
    ([companies, selectedCompanyId]) => {
      // set selected company
      let companyToSelect: Company;
      if (exists(selectedCompanyId) && companies.find(c => c.id === Number(selectedCompanyId))) {
        companyToSelect = companies.find(c => c.id === Number(selectedCompanyId));
      } else if (companies.length > 0) {
        companyToSelect = companies[0];
      }
      if (exists(companyToSelect)) {
        this.connectToSelectedCompany(companyToSelect);
      }
    }
  );

  companyMenuThemes$ = combineLatest([this.menuDomainModel.themes$.notNull$(), this.selectedCompany$]).pipe(
    map(([themes, company]) => themes?.filter(t => t?.usedByCompanyIds?.includes(company?.id)))
  );

  // Search Bar
  companiesAndLocations: CompaniesAndLocations[] = [];
  searchBarValue$ = new BehaviorSubject<string>(null);

  private _posProviderFilter = new BehaviorSubject<InventoryProvider>(null);
  public posProviderFilter$ = this._posProviderFilter as Observable<InventoryProvider>;
  setPosProviderFilter = (inventoryProvider: InventoryProvider) => this._posProviderFilter.next(inventoryProvider);

  constructor(
    private router: Router,
    private domainModel: CompanyDomainModel,
    private menuDomainModel: MenuDomainModel
  ) {
    super();
    this.setupBindings();
    this.setupSearchableItems();
  }

  public companiesFilteredByPOSProvider$ = combineLatest([this.companies$, this.posProviderFilter$]).pipe(
    map(([companies, posProviderFilter]) => {
      return exists(posProviderFilter) ? companies?.filter(c => c?.provider === posProviderFilter) : companies;
    })
  );

  public fuzzyResults$ = combineLatest([this.companiesFilteredByPOSProvider$, this.searchBarValue$]).pipe(
    debounceTime(50),
    map(([companies, searchText]) => {
      return searchText?.length >= 2 ? SearchUtils.searchCompanyAndLocations(companies, searchText) : companies;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  setupBindings() {
    // Bind to state when no companies are set yet
    const compSub = this.domainModel.companies$.subscribe(companies => {
      const lm = 'Loading Companies';
      if (!companies && !this._loadingOpts.containsRequest(lm)) this._loadingOpts.addRequest(lm);
      if (exists(companies)) this._loadingOpts.removeRequest(lm);
    });
    this.pushSub(compSub);
  }

  setupSearchableItems() {
    this.companies$.subscribe(companies => {
      companies?.forEach(c => {
        c?.locations?.forEach(l => {
          const newLocation = new CompaniesAndLocations();
          newLocation.lookUpKey = l.name.replace(/[^a-zA-Z0-9 ]/g, '') + ' in ' + c.name.replace(/[^a-zA-Z0-9 ]/g, '');
          newLocation.companyId = c.id;
          newLocation.locationId = l.id;
          this.companiesAndLocations.push(newLocation);
        });
        const newCompany = new CompaniesAndLocations();
        newCompany.lookUpKey = c.name.replace(/[^a-zA-Z0-9 ]/g, '');
        newCompany.companyId = c.id;
        newCompany.locationId = null;
        this.companiesAndLocations.push(newCompany);
      });
      this.companiesAndLocations.sort((a, b) => SortUtils.NUMERICAL_STRING_COMPARE(a?.lookUpKey, b?.lookUpKey));
    });
  }

  companyRowClicked(c: Company) {
    this.companyIdToSelect$.next(c.id);
  }

  resetSearchTextFilter() {
    this.searchBarValue$.next('');
  }
}
